import { useCallback, useState } from "react";

import { useJitsu } from "@jitsu/react";
import { captureException } from "@sentry/react";
import { toast } from "react-hot-toast";

import { freshlyUploadedFileVar } from "@/client/apollo";
import {
  createMultipartUpload,
  MultipartUploadHandle,
} from "@/client/media-upload";
import { JitsuEvent } from "@/data/events/JitsuEvent";
import {
  ResourceFragment,
  useCreateResourceMutation,
} from "@/generated/graphql";
import { addResourceToCache } from "@/ui/task/resources/hooks";

type OnUploadFile = (
  name: string,
  file: File
) => Promise<ResourceFragment | undefined>;
type CallbackTuple = [OnUploadFile, boolean];

export function useUploadResource(
  assessmentId: string,
  taskId: string
): CallbackTuple {
  const { track } = useJitsu();
  // Tracking number of network requests in flight
  const [inFlight, setInFlight] = useState(0);
  const [createResource] = useCreateResourceMutation({
    update(cache, { data }) {
      if (data && data.createResource) {
        addResourceToCache(cache, assessmentId, taskId, data.createResource);
      }
    },
    onCompleted: ({ createResource: resource }) => {
      track(JitsuEvent.RESOURCE_ADDED, {
        resource_id: resource.id,
        resource_name: resource.name,
        resource_url: resource.url,
        resource_file_size: resource.fileSize,
      });
    },
  });
  const callback = useCallback<OnUploadFile>(
    async (name, file) => {
      setInFlight((n) => n + 1);
      let upload: MultipartUploadHandle | undefined;
      try {
        upload = createMultipartUpload(file, assessmentId);
        const mediaData = await upload.start();

        const { data } = await createResource({
          variables: {
            assessmentId,
            taskId,
            name,
            url: "",
            mediaId: mediaData.id,
          },
        });

        const resource = data?.createResource;
        if (!resource) {
          throw new Error("Failed to create resource");
        }

        freshlyUploadedFileVar(resource.id);

        return resource;
      } catch (err) {
        // No retry UI here, so abort instead of keeping the upload for resume —
        // leaves no orphaned multipart parts in S3.
        upload?.cancel();
        toast.error(
          "Upload failed. Check your internet connection and try again."
        );
        captureException(`resource upload error: ${err}`);
      } finally {
        // createResource can throw after a successful upload — decrement once.
        setInFlight((n) => n - 1);
      }
      return undefined;
    },
    [assessmentId, createResource, taskId]
  );

  return [callback, inFlight > 0];
}
